import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Link, withTranslation } from 'i18n';
import getConfig from 'next/config';

import Title from 'components/Title/Title';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import GoogleLogin from 'components/GoogleLogin';

import Input from 'components/CustomField/Formik/Input';
import Buttonv2 from 'components/_v2/atoms/buttons/Button';
import { useExtension } from '../../../../hooks/useExtension';

const initialValues = {
  email: '',
  password: '',
};

const { publicRuntimeConfig } = getConfig();
const isOneVision = publicRuntimeConfig?.clientName === 'onevision';

const validationSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t('error-email')).required(t('error-email-required')),
    password: Yup.string().required(t('error-password-required')),
  });

const isEmailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ConnectionForm = ({
  t,
  ssoGoogle,
  authorizedDomainNames,
  setUseSso,
  TopErrorComponent,
  BottomErrorComponent,
  setError,
  signIn,
}) => {
  const SsoButton = useExtension('SsoButton');
  const featureFlags = useExtension('FeatureFlags');
  const isPaas = featureFlags?.isPaas;
  const [hasEmailFormatError, setHasEmailFormatError] = useState(false);

  const clearInlineError = useCallback(() => {
    setHasEmailFormatError(false);
    setError(null);
  }, [setError]);

  const handleLogin = useCallback((e, values) => {
    e.preventDefault();
    const email = values.email.trim();
    const password = values.password.trim();

    if (!email || !password) {
      setError(t('error-message-connection'));
      setHasEmailFormatError(false);
    } else if (!isEmailValid(email)) {
      setError(t('error-email'));
      setHasEmailFormatError(true);
    } else {
      clearInlineError();
      signIn({ email, password });
    }
  }, [clearInlineError, setError, signIn, t]);

  const handleValidate = useCallback((values) => {
    if (hasEmailFormatError && isEmailValid(values.email.trim())) {
      clearInlineError();
    }

    return {};
  }, [clearInlineError, hasEmailFormatError]);

  return (
    <div className="m-auto flex max-w-sm flex-col justify-center gap-8">
      <div className="flex w-full justify-center border-b border-solid border-gray-500 pb-4">
        <Title size="h3" className="text-black">
          {t('connection-form-title')}
        </Title>
      </div>
      {TopErrorComponent}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(t)}
        validate={handleValidate}
      >
        {({ values }) => (
          <form className="flex flex-col gap-6" onSubmit={(e) => handleLogin(e, values)}>
            {isOneVision && (
              <>
                <Title size="h4" className="m-auto text-primary-500">
                  {t('renault-group')}
                </Title>
                {SsoButton({ t, setUseSso })}
                <div className="h-px w-full bg-gray-300" />
                <Title size="h4" className="m-auto text-primary-500">
                  {t('external-users')}
                </Title>
                <Text>{t('only-no-ipn')}</Text>
              </>
            )}
            <Input id="email" name="email" data-cy="email" label={t('label-email')} required />
            <Input
              id="password"
              name="password"
              label={t('label-password')}
              type="password"
              required
              autoComplete="new-password"
              data-cy="password"
            />
            {isPaas ? (
              <>
                <Button
                  onClick={(e) => handleLogin(e, values)}
                  appearance={isOneVision ? 'light' : 'primary'}
                  type="submit"
                >
                  {t('connect-button')}
                </Button>
                <Text className="text-info-500">
                  <Link href="/forgot-password">{t('forgot-password')}</Link>
                </Text>
              </>
            ) : (
              <>
                <Buttonv2
                  onClick={(e) => handleLogin(e, values)}
                  variant="kazaarPrimary"
                  type="submit"
                >
                  {t('connect-button')}
                </Buttonv2>
                <Text className="text-kazaarBrand-400">
                  <Link href="/forgot-password">{t('forgot-password')}</Link>
                </Text>
              </>
            )}

            {BottomErrorComponent}
            {!isOneVision && (
              <>
                <div className="h-px w-full bg-gray-300" />
                {ssoGoogle && (
                  <GoogleLogin authorizedDomainNames={authorizedDomainNames} setError={setError} />
                )}
                {SsoButton({ t, setUseSso })}
              </>
            )}
          </form>
        )}
      </Formik>
    </div>
  );
};

ConnectionForm.defaultProps = {
  ssoGoogle: null,
  TopErrorComponent: null,
  BottomErrorComponent: null,
};

ConnectionForm.propTypes = {
  t: PropTypes.func.isRequired,
  setUseSso: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  signIn: PropTypes.func.isRequired,
  TopErrorComponent: PropTypes.node,
  BottomErrorComponent: PropTypes.node,
  ssoGoogle: PropTypes.bool,
  authorizedDomainNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ConnectionForm.getInitialProps = async () => ({
  namespacesRequired: ['connectionform', 'common'],
});

export default withTranslation('connectionform')(ConnectionForm);
